/**
 * Custom React hook for 3Dmol.js molecular visualization
 *
 * This hook provides a clean API for:
 * - Initializing and managing the 3Dmol viewer lifecycle
 * - Loading PDB and other molecular formats
 * - Applying styles and color schemes
 * - Domain/region highlighting on protein structures
 * - pLDDT confidence coloring for AlphaFold structures
 * - Residue selection and interaction
 *
 * Reference: https://3dmol.csb.pitt.edu/doc/index.html
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import type {
  GLViewer,
  AtomSelectionSpec,
  StyleSpec,
  CartoonStyleSpec,
  LabelSpec,
  AtomSpec,
} from '3dmol'

// Import 3Dmol from npm package
// Note: 3Dmol.js also supports CDN loading via window.$3Dmol
let $3Dmol: typeof import('3dmol') | null = null

// Lazy load 3Dmol.js
async function load3Dmol(): Promise<typeof import('3dmol')> {
  if ($3Dmol) return $3Dmol
  $3Dmol = await import('3dmol')
  return $3Dmol
}

/**
 * Domain/region to highlight on the structure
 */
export interface DomainHighlight {
  /** Domain name for label */
  name: string
  /** Start residue number */
  resStart: number
  /** End residue number */
  resEnd: number
  /** Color (hex string) */
  color: string
  /** Optional chain ID */
  chain?: string
}

/**
 * Functional site to highlight with stick representation
 */
export interface SiteHighlight {
  /** Site type/name */
  siteType: string
  /** Residue positions (array of residue numbers) */
  residues: number[]
  /** Source domain (optional context) */
  sourceDomain?: string
  /** Optional chain ID */
  chain?: string
  /** Color (hex string, default: magenta) */
  color?: string
}

/**
 * Residue selection
 */
export interface ResidueSelection {
  chain: string
  resi: number
  resn?: string
}

/**
 * Options for the 3Dmol viewer hook
 */
export interface Use3DmolViewerOptions {
  /** Background color (default: transparent) */
  backgroundColor?: string
  /** Background opacity (0-1, default: 0) */
  backgroundOpacity?: number
  /** Enable antialiasing (default: true) */
  antialias?: boolean
  /** Callback when atom is clicked */
  onAtomClick?: (atom: AtomSpec) => void
  /** Callback when residue is clicked */
  onResidueClick?: (residue: ResidueSelection) => void
  /** Callback when atom is hovered */
  onAtomHover?: (atom: AtomSpec | null) => void
}

/**
 * Predefined color schemes
 */
export const COLOR_SCHEMES = {
  /** Rainbow gradient from N to C terminus */
  spectrum: { color: 'spectrum' as const },
  /** Color by chain */
  chain: { colorscheme: 'chainHetatm' as const },
  /** Secondary structure (helix=magenta, sheet=yellow, coil=white) */
  secondary: { colorscheme: 'ssJmol' as const },
  /** Hydrophobicity (Jmol colors) */
  hydrophobicity: { colorscheme: 'Jmol' as const },
  /** Amino acid type */
  amino: { colorscheme: 'amino' as const },
}

/**
 * AlphaFold pLDDT color scheme constants
 * pLDDT scores are stored in B-factor column
 */
export const PLDDT_COLORS = {
  veryHigh: '#0D56D3', // > 90
  confident: '#6ACBF1', // 70-90
  low: '#FED936', // 50-70
  veryLow: '#FD7D4D', // < 50
}

/**
 * pLDDT color scheme specification for 3Dmol.js
 */
export const PLDDT_COLORSCHEME = {
  prop: 'b', // B-factor column contains pLDDT
  gradient: 'roygb',
  min: 50,
  max: 90,
}

/**
 * Domain colors (cycle through for multiple domains)
 */
export const DOMAIN_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
]

/**
 * Custom hook for 3Dmol.js molecular visualization
 */
export function use3DmolViewer(
  containerRef: React.RefObject<HTMLDivElement>,
  options: Use3DmolViewerOptions = {}
) {
  const viewerRef = useRef<GLViewer | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const modelDataRef = useRef<string | null>(null)

  // Memoize options to prevent unnecessary re-renders
  const {
    backgroundColor = 'black',
    backgroundOpacity = 0,
    antialias = true,
    onAtomClick,
    onResidueClick,
    onAtomHover,
  } = options

  // Initialize viewer
  useEffect(() => {
    let mounted = true

    const initViewer = async () => {
      if (!containerRef.current) return

      try {
        setIsLoading(true)
        const lib = await load3Dmol()

        if (!mounted || !containerRef.current) return

        // Create viewer
        const viewer = lib.createViewer(containerRef.current, {
          backgroundColor,
          backgroundAlpha: backgroundOpacity,
          antialias,
        })

        viewerRef.current = viewer
        setIsReady(true)
        setError(null)
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize 3D viewer'))
        }
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    initViewer()

    return () => {
      mounted = false
      if (viewerRef.current) {
        viewerRef.current.clear()
        viewerRef.current = null
      }
      setIsReady(false)
    }
  }, [containerRef, backgroundColor, backgroundOpacity, antialias])

  // Load PDB data
  const loadPDB = useCallback(
    (pdbData: string, format: string = 'pdb') => {
      if (!viewerRef.current) return false

      try {
        viewerRef.current.removeAllModels()
        viewerRef.current.addModel(pdbData, format)
        modelDataRef.current = pdbData

        // Set up click handlers if provided
        if (onAtomClick || onResidueClick) {
          viewerRef.current.setClickable({}, true, (atom: AtomSpec) => {
            if (onAtomClick) {
              onAtomClick(atom)
            }
            if (onResidueClick && atom.chain && atom.resi !== undefined) {
              onResidueClick({
                chain: atom.chain,
                resi: atom.resi,
                resn: atom.resn,
              })
            }
          })
        }

        // Set up hover handlers if provided
        if (onAtomHover) {
          viewerRef.current.setHoverable(
            {},
            true,
            (atom: AtomSpec) => onAtomHover(atom),
            () => onAtomHover(null)
          )
        }

        return true
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load structure'))
        return false
      }
    },
    [onAtomClick, onResidueClick, onAtomHover]
  )

  // Load from URL
  const loadFromUrl = useCallback(
    async (url: string, format: string = 'pdb') => {
      try {
        setIsLoading(true)
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`Failed to fetch structure: ${response.statusText}`)
        }
        const data = await response.text()
        const success = loadPDB(data, format)
        setIsLoading(false)
        return success
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load structure from URL'))
        setIsLoading(false)
        return false
      }
    },
    [loadPDB]
  )

  // Apply style to all or selected atoms
  const setStyle = useCallback(
    (selector: AtomSelectionSpec, style: StyleSpec) => {
      if (!viewerRef.current) return
      viewerRef.current.setStyle(selector, style)
    },
    []
  )

  // Apply cartoon style with color scheme
  const applyCartoonStyle = useCallback(
    (
      colorScheme: keyof typeof COLOR_SCHEMES | 'plddt' | 'custom',
      customStyle?: CartoonStyleSpec
    ) => {
      if (!viewerRef.current) return

      let cartoonStyle: CartoonStyleSpec

      if (colorScheme === 'plddt') {
        cartoonStyle = { colorscheme: PLDDT_COLORSCHEME }
      } else if (colorScheme === 'custom' && customStyle) {
        cartoonStyle = customStyle
      } else if (colorScheme in COLOR_SCHEMES) {
        cartoonStyle = COLOR_SCHEMES[colorScheme as keyof typeof COLOR_SCHEMES]
      } else {
        cartoonStyle = COLOR_SCHEMES.spectrum
      }

      viewerRef.current.setStyle({ hetflag: false }, { cartoon: cartoonStyle })
      // Show heteroatoms (ligands) as sticks
      viewerRef.current.setStyle({ hetflag: true }, { stick: {} })
    },
    []
  )

  // Apply stick style
  const applyStickStyle = useCallback((radius: number = 0.15) => {
    if (!viewerRef.current) return
    viewerRef.current.setStyle({}, { stick: { radius } })
  }, [])

  // Apply sphere style
  const applySphereStyle = useCallback((radius: number = 0.5) => {
    if (!viewerRef.current) return
    viewerRef.current.setStyle({}, { sphere: { radius } })
  }, [])

  // Add molecular surface
  const addSurface = useCallback(
    async (
      type: 'VDW' | 'MS' | 'SAS' | 'SES' = 'VDW',
      opacity: number = 0.8,
      color?: string
    ) => {
      if (!viewerRef.current) return null

      const lib = await load3Dmol()
      const surfaceStyle: any = { opacity }
      if (color) {
        surfaceStyle.color = color
      }

      return viewerRef.current.addSurface(lib.SurfaceType[type], surfaceStyle)
    },
    []
  )

  // Remove all surfaces
  const removeSurfaces = useCallback(() => {
    viewerRef.current?.removeAllSurfaces()
  }, [])

  // Highlight residue range (for domain visualization)
  const highlightResidueRange = useCallback(
    (startResi: number, endResi: number, color: string, chain?: string) => {
      if (!viewerRef.current) return

      const selector: AtomSelectionSpec = {
        resi: `${startResi}-${endResi}`,
        ...(chain && { chain }),
      }

      viewerRef.current.setStyle(selector, {
        cartoon: { color },
      })
    },
    []
  )

  // Highlight multiple domains
  const highlightDomains = useCallback(
    (domains: DomainHighlight[], baseColorScheme: keyof typeof COLOR_SCHEMES = 'secondary') => {
      if (!viewerRef.current) return

      // First apply base style to all protein atoms
      const baseStyle = COLOR_SCHEMES[baseColorScheme] || COLOR_SCHEMES.secondary
      viewerRef.current.setStyle({ hetflag: false }, { cartoon: baseStyle })

      // Then highlight each domain
      domains.forEach((domain) => {
        const selector: AtomSelectionSpec = {
          resi: `${domain.resStart}-${domain.resEnd}`,
          ...(domain.chain && { chain: domain.chain }),
        }

        viewerRef.current!.setStyle(selector, {
          cartoon: { color: domain.color },
        })
      })

      // Show heteroatoms as sticks
      viewerRef.current.setStyle({ hetflag: true }, { stick: {} })
    },
    []
  )

  // Color by pLDDT (AlphaFold confidence)
  const colorByPLDDT = useCallback(() => {
    if (!viewerRef.current) return

    viewerRef.current.setStyle(
      { hetflag: false },
      {
        cartoon: {
          colorscheme: PLDDT_COLORSCHEME,
        },
      }
    )
    // Show heteroatoms as sticks
    viewerRef.current.setStyle({ hetflag: true }, { stick: {} })
  }, [])

  // Highlight functional sites as stick representation
  const highlightSites = useCallback(
    (sites: SiteHighlight[], showSticks: boolean = true) => {
      if (!viewerRef.current) return

      sites.forEach((site) => {
        if (!site.residues || site.residues.length === 0) return

        // Select all residues for this site
        site.residues.forEach((resi) => {
          const selector: AtomSelectionSpec = {
            resi,
            ...(site.chain && { chain: site.chain }),
          }

          if (showSticks) {
            // Add stick representation for site residues (overlay on existing style)
            viewerRef.current!.addStyle(selector, {
              stick: {
                radius: 0.2,
                color: site.color || '#FF00FF', // Magenta default
              },
            })
          }
        })
      })
    },
    []
  )

  // Add text label
  const addLabel = useCallback(
    (
      text: string,
      position: { x: number; y: number; z: number },
      options?: Partial<LabelSpec>
    ) => {
      if (!viewerRef.current) return null

      return viewerRef.current.addLabel(text, {
        position,
        backgroundColor: '#000000',
        backgroundOpacity: 0.7,
        fontColor: '#ffffff',
        fontSize: 12,
        ...options,
      })
    },
    []
  )

  // Add domain labels
  const addDomainLabels = useCallback(
    (domains: DomainHighlight[]) => {
      if (!viewerRef.current) return

      viewerRef.current.removeAllLabels()

      domains.forEach((domain) => {
        // Calculate center position of domain (rough approximation)
        const centerResi = Math.floor((domain.resStart + domain.resEnd) / 2)

        // Get atom at center residue for position
        const model = viewerRef.current!.getModel()
        if (model) {
          const atoms = model.selectedAtoms({
            resi: centerResi,
            atom: 'CA', // Alpha carbon
            ...(domain.chain && { chain: domain.chain }),
          })

          if (atoms.length > 0) {
            const atom = atoms[0]
            viewerRef.current!.addLabel(domain.name, {
              position: { x: atom.x!, y: atom.y!, z: atom.z! },
              backgroundColor: domain.color,
              backgroundOpacity: 0.8,
              fontColor: '#ffffff',
              fontSize: 12,
              inFront: true,
            })
          }
        }
      })
    },
    []
  )

  // Clear all labels
  const clearLabels = useCallback(() => {
    viewerRef.current?.removeAllLabels()
  }, [])

  // Zoom to fit all or selection
  const zoomTo = useCallback((selector?: AtomSelectionSpec, duration?: number) => {
    if (!viewerRef.current) return
    viewerRef.current.zoomTo(selector, duration)
  }, [])

  // Zoom to specific residue range
  const zoomToResidueRange = useCallback(
    (startResi: number, endResi: number, chain?: string, duration?: number) => {
      if (!viewerRef.current) return

      const selector: AtomSelectionSpec = {
        resi: `${startResi}-${endResi}`,
        ...(chain && { chain }),
      }

      viewerRef.current.zoomTo(selector, duration)
    },
    []
  )

  // Render the scene
  const render = useCallback(() => {
    viewerRef.current?.render()
  }, [])

  // Reset view
  const resetView = useCallback(() => {
    if (!viewerRef.current) return
    viewerRef.current.zoomTo()
    viewerRef.current.render()
  }, [])

  // Take screenshot
  const takeScreenshot = useCallback((): string | null => {
    if (!viewerRef.current) return null
    return viewerRef.current.pngURI()
  }, [])

  // Download screenshot
  const downloadScreenshot = useCallback((filename: string = 'structure.png') => {
    const uri = takeScreenshot()
    if (!uri) return

    const link = document.createElement('a')
    link.download = filename
    link.href = uri
    link.click()
  }, [takeScreenshot])

  // Rotate view
  const rotate = useCallback((angle: number, axis: 'x' | 'y' | 'z' = 'y', duration?: number) => {
    viewerRef.current?.rotate(angle, axis, duration)
  }, [])

  // Resize canvas
  const resize = useCallback(() => {
    viewerRef.current?.resize()
  }, [])

  // Clear all
  const clear = useCallback(() => {
    if (!viewerRef.current) return
    viewerRef.current.clear()
    modelDataRef.current = null
  }, [])

  return {
    // State
    isReady,
    isLoading,
    error,
    viewer: viewerRef.current,

    // Loading
    loadPDB,
    loadFromUrl,

    // Styling
    setStyle,
    applyCartoonStyle,
    applyStickStyle,
    applySphereStyle,
    addSurface,
    removeSurfaces,

    // Domain highlighting
    highlightResidueRange,
    highlightDomains,
    colorByPLDDT,

    // Site highlighting
    highlightSites,

    // Labels
    addLabel,
    addDomainLabels,
    clearLabels,

    // View control
    zoomTo,
    zoomToResidueRange,
    render,
    resetView,
    rotate,
    resize,

    // Screenshot
    takeScreenshot,
    downloadScreenshot,

    // Cleanup
    clear,
  }
}

export default use3DmolViewer
